import React, {useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {OrderSummary as OrderSummaryComponent, DEFAULT_TABS} from './index'
import {buildLocationOrderSummary} from '../../pages/CatalogSelection/utils/orderSummaryUtils'
import {
  SummaryWrapper,
  SummaryBody,
  SummaryActionsSlot,
  LoadingOverlay,
} from './OrderSummaryContainer.styles'
import {Spinner} from '../../components/loader-spinner'
import {CouponSearch} from '../CouponSearch'

// The account-header "Create Purchase Link" flow's Order Summary: unlike
// OrderSummaryContainer (which shows only the single active location), this
// renders every parent-pay location from the dropdown as its own
// independently-collapsible accordion (see ParentPayLocationSelector) with a
// combined total, using the same buildLocationOrderSummary pricing rules per
// location. Editing (removing an item/coupon, the coupon-search footer)
// stays scoped to whichever location is currently active in the catalog
// panel above — matches the confirmed "one location shopped at a time"
// model; other locations' totals reflect whatever was left in their cart
// the last time they were active.
export function MultiLocationOrderSummaryContainer({
  locations,
  activeLocationId,
  selectedCountry,
  selectedProducts,
  handleRemoveProduct,
  isNewTLMCAccessible,
  isNewCustomer,
  isUpgrade = false,
  // Opt-in (PurchaseLink wizard): auto-apply DB-configured new-account coupons.
  applyNewAccountDefaults = false,
  userRoles,
  refreshPaymentPreview,
  onUpdateItem,
  onPreviewLoadingChange,
  isActiveLocationLoaded = false,
  flushEdges = false,
  actions,
}) {
  const activeIndex = locations.findIndex(
    l => l.locationId === activeLocationId,
  )
  const activeLocation = activeIndex >= 0 ? locations[activeIndex] : null
  const manualCoupons = activeLocation?.coupons ?? []

  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const pendingPreviewCountRef = useRef(0)
  // See OrderSummaryContainer's identical guard — Catalog Selection's
  // Continue button can navigate away (to Scheduling) while a preview
  // refresh is still in flight.
  const isMountedRef = useRef(true)
  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    [],
  )

  const startPreviewLoading = () => {
    pendingPreviewCountRef.current++
    if (!isMountedRef.current) return
    setIsPreviewLoading(true)
    onPreviewLoadingChange?.(true)
  }

  const endPreviewLoading = () => {
    pendingPreviewCountRef.current--
    if (pendingPreviewCountRef.current === 0 && isMountedRef.current) {
      setIsPreviewLoading(false)
      onPreviewLoadingChange?.(false)
    }
  }

  // Same fingerprint-and-skip-on-switch pattern as OrderSummaryContainer,
  // scoped to the active location only.
  const couponFingerprint = (activeLocation?.coupons ?? [])
    .map(c => c.code ?? c.couponCode)
    .join(',')

  const prevCouponStateRef = useRef(null)
  useEffect(() => {
    const prev = prevCouponStateRef.current
    prevCouponStateRef.current = {id: activeLocationId, couponFingerprint}
    if (prev === null) return
    if (prev.id !== activeLocationId) return
    startPreviewLoading()
    refreshPaymentPreview().finally(endPreviewLoading)
    // activeLocationId is a dependency so the stored id is refreshed on every
    // switch. With only the fingerprint, switching between two locations whose
    // coupons are identical (e.g. both empty) never ran this effect, so the id
    // stayed stale and the next real coupon change (the auto-applied defaults
    // attaching to the new location) was mistaken for a switch and skipped:
    // the location was priced without the coupons it lists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocationId, couponFingerprint])

  const itemsFingerprint = (activeLocation?.items ?? [])
    .map(i => {
      const addonsPart = (i.addons ?? [])
        .map(a => `${a.productCode}:${a.quantity ?? 1}`)
        .join('+')
      const configPart = i.configuration?.proposalAmount
        ? `:${i.configuration.proposalAmount}`
        : ''
      return `${i.productCode}:${i.quantity ?? 1}${configPart}${
        addonsPart ? `|${addonsPart}` : ''
      }`
    })
    .join(',')

  const prevItemsStateRef = useRef(null)
  useEffect(() => {
    const prev = prevItemsStateRef.current
    prevItemsStateRef.current = {id: activeLocationId, itemsFingerprint}
    if (prev === null) return
    if (prev.id !== activeLocationId) return
    startPreviewLoading()
    refreshPaymentPreview().finally(endPreviewLoading)
    // activeLocationId: see the coupon effect above (same stale-id problem when
    // two locations have identical carts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocationId, itemsFingerprint])

  const hasRefreshedItemRef = useRef(null)
  useEffect(() => {
    if (!isActiveLocationLoaded) return
    if (hasRefreshedItemRef.current === activeLocationId) return
    hasRefreshedItemRef.current = activeLocationId
    if (!activeLocation?.items?.length) return
    startPreviewLoading()
    refreshPaymentPreview().finally(endPreviewLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveLocationLoaded, activeLocationId])

  const [activeTab, setActiveTab] = useState(DEFAULT_TABS[0]?.id)
  const [openLocations, setOpenLocations] = useState(() =>
    activeIndex >= 0 ? {[activeIndex]: true} : {},
  )
  // Force-open (never force-close) whichever location just became active,
  // so switching the dropdown always reveals that location's section.
  useEffect(() => {
    if (activeIndex < 0) return
    setOpenLocations(prev =>
      prev[activeIndex] ? prev : {...prev, [activeIndex]: true},
    )
  }, [activeIndex])
  const [softwareOpen, setSoftwareOpen] = useState({0: true})
  const [addonsOpen, setAddonsOpen] = useState({})
  const [oneTimeFeeOpen, setOneTimeFeeOpen] = useState({})
  const [couponsOpen, setCouponsOpen] = useState({})

  const isMonthlyTab = activeTab === 'monthly'
  const dueTodayIncludesTax =
    activeLocation?.dueTodayIncludesTax ?? !!activeLocation?.thryvId

  const summaries = locations.map(loc =>
    buildLocationOrderSummary(loc.items, {isMonthlyTab, isNewTLMCAccessible}),
  )

  const renderLocations = locations.map((loc, i) => ({
    locationId: loc.locationId,
    name: loc.locationName || loc.thryvId || '',
    softwareItems: summaries[i].softwareItems,
    addonItems: summaries[i].addonItems,
    oneTimeFeeItems: summaries[i].oneTimeFeeItems,
    itemCount: summaries[i].itemCount,
    total: summaries[i].total,
    coupons: loc.coupons ?? [],
    // Distinguishes "this location's subscriptions haven't loaded yet" from
    // "this location genuinely has 0 items" — without it the row briefly
    // renders as a real-looking $0.00/0-items summary while its preload
    // fetch (usePurchaseLinkPreloadAllLocations) is still in flight.
    isLoaded: loc.isLoaded !== false,
  }))

  const total = summaries.reduce((sum, s) => sum + s.total, 0)
  const monthlyTotal = summaries.reduce((sum, s) => sum + s.monthlyTotal, 0)
  const tabItemCounts = {
    today: summaries.reduce((sum, s) => sum + s.tabItemCounts.today, 0),
    monthly: summaries.reduce((sum, s) => sum + s.tabItemCounts.monthly, 0),
  }
  const tabTotals = {today: total, monthly: monthlyTotal}

  const productCodes = selectedProducts.map(i => i?.planCode)
  const autoCouponProductCodes = selectedProducts
    .filter(i => !i?.alreadyIncluded)
    .map(i => i?.planCode)

  const handleCouponsChange = updatedCoupons => {
    if (activeLocationId == null) return
    onUpdateItem(activeLocationId, {coupons: updatedCoupons})
  }

  // Editing stays scoped to the active location — other locations' rows in
  // the summary are display-only until the user switches to them.
  const onlyIfActive = fn => (item, j, i) => {
    if (locations[i]?.locationId !== activeLocationId) {
      toast.info(
        'Switch to this location in the catalog dropdown to make changes.',
      )
      return
    }
    fn(item, j, i)
  }

  return (
    <SummaryWrapper hasActions={Boolean(actions)}>
      {isPreviewLoading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SummaryBody hasActions={Boolean(actions)}>
        <OrderSummaryComponent
          locations={renderLocations}
          total={total}
          tabs={DEFAULT_TABS}
          flushEdges={flushEdges}
          country={selectedCountry || 'US'}
          isUpgrade={isUpgrade}
          dueTodayIncludesTax={dueTodayIncludesTax}
          tabItemCounts={tabItemCounts}
          tabTotals={tabTotals}
          singleLocation={false}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          openLocations={openLocations}
          onToggleLocation={i =>
            setOpenLocations(prev => ({...prev, [i]: !prev[i]}))
          }
          softwareOpen={softwareOpen}
          addonsOpen={addonsOpen}
          oneTimeFeeOpen={oneTimeFeeOpen}
          onToggleSoftware={i =>
            setSoftwareOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onToggleAddons={i =>
            setAddonsOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onToggleOneTimeFee={i =>
            setOneTimeFeeOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onRemoveSoftwareItem={onlyIfActive(
            item => item.productCode && handleRemoveProduct(item.productCode),
          )}
          onRemoveAddonItem={onlyIfActive(
            item => item.productCode && handleRemoveProduct(item.productCode),
          )}
          onRemoveOneTimeFeeItem={onlyIfActive(
            item => item.productCode && handleRemoveProduct(item.productCode),
          )}
          couponsOpen={couponsOpen}
          onToggleCoupons={i =>
            setCouponsOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onRemoveCoupon={onlyIfActive((coupon, j, i) => {
            const targetCode = coupon.code ?? coupon.couponCode
            onUpdateItem(locations[i].locationId, {
              coupons: (locations[i].coupons ?? []).filter(
                c => (c.code ?? c.couponCode) !== targetCode,
              ),
            })
          })}
          footer={
            <CouponSearch
              key={activeLocationId}
              country={selectedCountry || 'US'}
              productCodes={productCodes}
              autoCouponProductCodes={autoCouponProductCodes}
              thryvId={activeLocation?.thryvId}
              isNewCustomer={isNewCustomer}
              applyNewAccountDefaults={applyNewAccountDefaults}
              isUpgradeJourney={isUpgrade}
              userRoles={userRoles}
              initialCoupons={manualCoupons}
              onCouponsChange={handleCouponsChange}
            />
          }
        />
      </SummaryBody>
      {actions && <SummaryActionsSlot>{actions}</SummaryActionsSlot>}
    </SummaryWrapper>
  )
}
